import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { CityKeySchema } from '../city-key/city-key';
import { VenuePhotoSchema } from '../venue-photo/venue-photo';

/**
 * A partner's meeting place as a **member** reads it (P25e).
 *
 * ## Why this is not `VenueSchema`
 *
 * `VenueSchema` is the staff payload, and its own docblock draws the line this
 * schema keeps: the contact fields are *"the operational handle on a partner —
 * who to telephone when a supervised meeting goes wrong — not content a member
 * reads … not part of any member payload this contract will grow."* `isActive`
 * belongs to the same half: publication is a staff verb, and every venue that
 * reaches a member is live by construction, so a boolean that is always `true`
 * would be a field carrying no information and one more thing to keep true.
 *
 * Both schemas are `.strict()`, which is what makes the line **enforced** rather
 * than intended — the shape P25d1 established with `AdminBookingSchema`, in the
 * other direction. A read that ever composed a member venue from a staff row
 * fails its own parse instead of quietly shipping a partner's telephone number.
 *
 * `createdAt`/`updatedAt` are absent for the same reason: when the operator
 * typed the row in is not a fact about the building.
 *
 * ## One schema, not a list row and a detail
 *
 * The browse list and the venue page answer the same question — *where is this,
 * and what does it look like* — so a second schema would be a level carrying no
 * information (P25d1's line, verbatim). The photographs travel on the list too,
 * because a directory of buildings drawn as text is an unfinished surface (U0);
 * they cost one **batched** read per page (`VenuePhotoRepository.listForVenues`),
 * never one per row, and each is an id plus a position rather than bytes.
 *
 * Each `mediaAssetId` is exchanged at `GET /media/:id/url`, the one authorization
 * path, which applies the `venue_photo` kind's `memberRead: 'open'` rule — the
 * axis P25a2 added for exactly this reader. Clients never construct a storage
 * url (N12).
 */
export const MemberVenueSchema = z
  .object({
    id: UuidSchema,
    /** What the browse filter facets on. The reader's label is `city`. */
    cityKey: CityKeySchema,
    name: z.string(),
    description: z.string(),
    /** The full street address, as a member would read it out to a driver. */
    address: z.string(),
    city: z.string(),
    /** Ordered by the venue's own `sortOrder`. Empty is a venue with no gallery. */
    photos: z.array(VenuePhotoSchema),
  })
  .strict();

export type MemberVenue = z.infer<typeof MemberVenueSchema>;
